import { useEffect, useState } from "react";
import { Reveal } from "@/components/Reveal";
import { SectionHeading } from "@/components/SectionHeading";
import { MapPin, Play, Pause, CloudRain, Loader2 } from "lucide-react";

const LAT = 52.5965;
const LON = 13.381;
const Z = 11;
const TILE = 256;

const WORLD = TILE * 2 ** Z;
const PX = ((LON + 180) / 360) * WORLD;
const LAT_RAD = (LAT * Math.PI) / 180;
const PY = ((1 - Math.log(Math.tan(LAT_RAD) + 1 / Math.cos(LAT_RAD)) / Math.PI) / 2) * WORLD;
const TILE_X = Math.floor(PX / TILE);
const TILE_Y = Math.floor(PY / TILE);
const OFF_X = Math.round(PX - TILE_X * TILE);
const OFF_Y = Math.round(PY - TILE_Y * TILE);
const GRID = [-2, -1, 0, 1, 2];

const basemapUrl = (x, y) => `https://tile.openstreetmap.org/${Z}/${x}/${y}.png`;

const TILE_M = 40075016.686 / 2 ** Z;
const MERC = 20037508.34;
const BBOX = [
  (TILE_X - 2) * TILE_M - MERC,
  MERC - (TILE_Y + 3) * TILE_M,
  (TILE_X + 3) * TILE_M - MERC,
  MERC - (TILE_Y - 2) * TILE_M,
];
const wmsUrl = (iso) =>
  `https://maps.dwd.de/geoserver/dwd/ows?SERVICE=WMS&VERSION=1.1.1&REQUEST=GetMap&LAYERS=dwd:Radar_rv_product_1x1km_ger&STYLES=&SRS=EPSG:3857&BBOX=${BBOX.join(",")}&WIDTH=1280&HEIGHT=1280&FORMAT=image/png&TRANSPARENT=TRUE&TIME=${iso}`;
const wmsStyle = {
  left: `calc(50% - ${OFF_X + 2 * TILE}px)`,
  top: `calc(50% - ${OFF_Y + 2 * TILE}px)`,
  width: 5 * TILE,
  height: 5 * TILE,
};

const tilePos = (dx, dy) => ({
  left: `calc(50% - ${OFF_X - dx * TILE}px)`,
  top: `calc(50% - ${OFF_Y - dy * TILE}px)`,
  width: TILE,
  height: TILE,
});

const TileGrid = ({ urlFor }) => (
  <>
    {GRID.map((dx) =>
      GRID.map((dy) => (
        <img
          key={`${dx}-${dy}`}
          src={urlFor(TILE_X + dx, TILE_Y + dy)}
          alt=""
          aria-hidden="true"
          loading="lazy"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
          className="absolute max-w-none select-none"
          style={tilePos(dx, dy)}
          draggable={false}
        />
      ))
    )}
  </>
);

export default function RainRadar() {
  const [frames, setFrames] = useState(null);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [hourly, setHourly] = useState([]);

  useEffect(() => {
    const r5 = Math.floor(Date.now() / 300000) * 300000;
    const list = [];
    for (let i = 11; i >= 1; i--) list.push({ time: r5 - i * 300000, kind: "past" });
    list.push({ time: r5, kind: "now" });
    const dayDE = (t) => new Date(t).toLocaleDateString("de-DE", { timeZone: "Europe/Berlin" });
    const todayDE = dayDE(r5);
    for (let i = 1; i <= 12; i++) {
      const t = r5 + i * 900000;
      if (dayDE(t) !== todayDE) break;
      list.push({ time: t, kind: "fc" });
    }
    setFrames(list);
    setIdx(11);

    fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${LAT}&longitude=${LON}&hourly=precipitation_probability,precipitation&timezone=Europe%2FBerlin&forecast_days=1`
    )
      .then((r) => r.json())
      .then((d) => {
        const nowH = new Date().getHours();
        const rows = [];
        (d.hourly?.time || []).forEach((t, i) => {
          const h = parseInt(t.slice(11, 13), 10);
          if (h >= nowH && rows.length < 12) {
            rows.push({
              h,
              prob: d.hourly.precipitation_probability?.[i] ?? 0,
              mm: d.hourly.precipitation?.[i] ?? 0,
            });
          }
        });
        setHourly(rows);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!playing || !frames || frames.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % frames.length), 900);
    return () => clearInterval(t);
  }, [playing, frames]);

  const active = frames?.[idx];
  const activeLabel = active
    ? new Date(active.time).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" })
    : "";
  const activeBadge = active?.kind === "fc" ? "Prognose" : active?.kind === "now" ? "Jetzt" : "Verlauf";

  return (
    <section id="regenradar" className="py-16 md:py-28" data-testid="radar-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          number="02"
          eyebrow="Regenradar"
          title="Regenradar für Berlin-Rosenthal"
          text="Der Radarfilm zeigt den Niederschlag der letzten Stunden und eine kurze Prognose – direkt über der Anlage (13158 Berlin). Darunter die stündliche Regenwahrscheinlichkeit für den Rest des Tages."
        />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <Reveal className="lg:col-span-7">
            <div
              className="relative overflow-hidden rounded-2xl border border-[#D8D3C5] bg-[#EFECE4] h-[320px] sm:h-[440px] shadow-[0_18px_40px_rgba(26,56,43,0.10)]"
              data-testid="radar-map"
            >
              <TileGrid urlFor={(x, y) => basemapUrl(x, y)} />
              {frames && active && (
                <img
                  key={active.time}
                  src={wmsUrl(new Date(active.time).toISOString())}
                  alt="Niederschlagsradar"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                  onLoad={(e) => {
                    e.currentTarget.style.display = "";
                  }}
                  className="absolute max-w-none select-none animate-[radarfade_0.5s_ease-out]"
                  style={wmsStyle}
                  draggable={false}
                />
              )}
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-[#C85A32] text-white shadow-lg ring-4 ring-white/60" data-testid="radar-pin">
                <MapPin size={17} />
              </div>
              <div className="absolute left-4 bottom-4 rounded-xl bg-[#1A382B]/90 backdrop-blur px-4 py-2.5 text-[#F8F7F2]" data-testid="radar-time">
                <span className="font-jbm text-sm font-semibold">{activeLabel || "--:--"}</span>
                <span className={`ml-2 rounded-full px-2.5 py-0.5 font-jbm text-[10px] tracking-wider uppercase ${activeBadge === "Prognose" ? "bg-[#D4A359] text-[#1A382B]" : "bg-white/15"}`}>
                  {activeBadge}
                </span>
              </div>
              <button
                onClick={() => setPlaying(!playing)}
                data-testid="radar-toggle"
                aria-label={playing ? "Slideshow pausieren" : "Slideshow abspielen"}
                className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-[#1A382B] text-[#F8F7F2] transition-transform hover:scale-105"
              >
                {playing ? <Pause size={16} /> : <Play size={16} />}
              </button>
              {!frames && (
                <div className="absolute inset-0 flex items-center justify-center gap-2 text-sm text-[#6F7E73]">
                  <Loader2 size={16} className="animate-spin" /> Radar wird geladen …
                </div>
              )}
              {frames && frames.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center text-sm text-[#6F7E73]" data-testid="radar-fallback">
                  Radar momentan nicht verfügbar.
                </div>
              )}
            </div>
            <p className="mt-3 font-jbm text-[10px] tracking-wider uppercase text-[#6F7E73]">
              Radar: © Deutscher Wetterdienst · Karte: © OpenStreetMap-Mitwirkende
            </p>
          </Reveal>

          <Reveal delay={0.12} className="lg:col-span-5">
            <div className="h-full rounded-2xl border border-[#D8D3C5] bg-white p-7 shadow-[0_18px_40px_rgba(26,56,43,0.08)]" data-testid="forecast-card">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#E2ECE5] text-[#1A382B]">
                  <CloudRain size={19} strokeWidth={1.8} />
                </span>
                <h3 className="font-display text-xl sm:text-2xl font-semibold text-[#1A382B]">Regenprognose heute</h3>
              </div>
              {hourly.length === 0 ? (
                <p className="mt-6 text-sm text-[#6F7E73]" data-testid="forecast-loading">Prognose wird geladen …</p>
              ) : (
                <div className="mt-6 flex items-end gap-1.5 sm:gap-2" data-testid="forecast-bars">
                  {hourly.map((r) => (
                    <div key={r.h} className="flex-1 flex flex-col items-center gap-1.5" data-testid={`forecast-hour-${r.h}`}>
                      <span className="font-jbm text-[9px] text-[#6F7E73]">{r.mm > 0 ? `${r.mm.toFixed(1)}` : ""}</span>
                      <div className="w-full h-24 flex items-end rounded-md bg-[#EFECE4] overflow-hidden">
                        <div
                          className={`w-full rounded-md transition-all duration-700 ${r.prob >= 50 ? "bg-[#C85A32]" : "bg-[#5A826B]"}`}
                          style={{ height: `${Math.max(r.prob, 3)}%` }}
                          title={`${r.prob} % · ${r.mm.toFixed(1)} mm`}
                        />
                      </div>
                      <span className="font-jbm text-[10px] text-[#47544A]">{String(r.h).padStart(2, "0")}h</span>
                    </div>
                  ))}
                </div>
              )}
              <p className="mt-4 text-xs leading-relaxed text-[#6F7E73]">
                Balken = Regenwahrscheinlichkeit in %, Zahl darüber = Niederschlagsmenge in mm pro Stunde.
                Daten: Open-Meteo für 13158 Berlin.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
